import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { CdkTrapFocus } from "@angular/cdk/a11y";

/**
 * Modal dialog. Declarative — toggle via `[open]`. The host element
 * itself renders nothing; when open, content is portaled into a CDK
 * overlay with backdrop, focus trap, and escape-to-close.
 *
 *   <bv-dialog [open]="confirmOpen" (closed)="confirmOpen = false">
 *     <bv-dialog-title>Delete tournament?</bv-dialog-title>
 *     <bv-dialog-body>This cannot be undone.</bv-dialog-body>
 *     <bv-dialog-footer>
 *       <button bv-button variant="ghost" (click)="confirmOpen = false">
 *         Cancel
 *       </button>
 *       <button bv-button variant="danger" (click)="confirmDelete()">
 *         Delete
 *       </button>
 *     </bv-dialog-footer>
 *   </bv-dialog>
 */
@Component({
    selector: "bv-dialog",
    templateUrl: "./dialog.component.html",
    styleUrls: ["./dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CdkTrapFocus]
})
export class BvDialogComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() ariaLabel?: string;
  @Input() closeOnBackdrop = true;
  @Input() closeOnEscape = true;

  @Output() closed = new EventEmitter<void>();

  @ViewChild("portal", { static: true })
  private portalTpl!: TemplateRef<unknown>;

  private overlayRef: OverlayRef | null = null;

  constructor(
    private overlay: Overlay,
    private vcr: ViewContainerRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes["open"]) return;
    if (this.open) {
      this.openOverlay();
    } else {
      this.closeOverlay();
    }
  }

  ngOnDestroy(): void {
    this.closeOverlay();
  }

  private openOverlay(): void {
    if (this.overlayRef) return;

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: "bv-dialog-backdrop",
      panelClass: "bv-dialog-panel",
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      disposeOnNavigation: true,
    });

    const portal = new TemplatePortal(this.portalTpl, this.vcr);
    this.overlayRef.attach(portal);

    if (this.closeOnBackdrop) {
      this.overlayRef.backdropClick().subscribe(() => this.requestClose());
    }
    if (this.closeOnEscape) {
      this.overlayRef.keydownEvents().subscribe((e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.preventDefault();
          this.requestClose();
        }
      });
    }
  }

  private closeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private requestClose(): void {
    this.open = false;
    this.closed.emit();
    this.closeOverlay();
  }
}

// Host-class scoping (`:host(.bv-dialog-title)`, etc.) keeps each sub-
// component's :host rule from leaking onto siblings — co-locating three
// inline-styled @Components in one file caused footer styles to apply to
// the body host at runtime, right-aligning the form content.
@Component({
    selector: "bv-dialog-title",
    template: "<ng-content></ng-content>",
    host: { class: "bv-dialog-title" },
    styles: [
        `
      :host(.bv-dialog-title) {
        display: flex;
        align-items: center;
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: 700;
        color: var(--text-primary);
        line-height: var(--leading-tight);
        padding: var(--space-3) var(--space-6);
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BvDialogTitleComponent {}

@Component({
    selector: "bv-dialog-body",
    template: "<ng-content></ng-content>",
    host: {
        class: "bv-dialog-body",
        "[class.bv-dialog-body--fill]": "fill",
    },
    styles: [
        `
      :host(.bv-dialog-body) {
        display: block;
        padding: var(--space-2) var(--space-6) var(--space-5);
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        color: var(--text-secondary);
        line-height: var(--leading-snug);
        overflow-y: auto;
        flex: 1 1 auto;
        min-height: 0;
      }
      /* Fill mode: the body itself does NOT scroll — it's a bounded flex
         column whose child owns the scroll region (e.g. the register-player
         member list under sticky tabs). Without this the default
         overflow-y:auto wins over a utility class (higher :host specificity)
         and you get a scrollbar-within-a-scrollbar. Declared after the base
         rule so it overrides at equal specificity. */
      :host(.bv-dialog-body--fill) {
        display: flex;
        flex-direction: column;
        padding: 0;
        overflow: hidden;
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BvDialogBodyComponent {
  /**
   * Fill mode — the body is a non-scrolling flex column whose child owns the
   * scroll region. Coerced so the standalone-attribute form (`<bv-dialog-body
   * fill>`) works; without coercion the empty-string attribute is falsy and
   * the modifier class never applies.
   */
  @Input()
  get fill(): boolean {
    return this._fill;
  }
  set fill(value: any) {
    this._fill = coerceBooleanProperty(value);
  }
  private _fill = false;
}

@Component({
    selector: "bv-dialog-footer",
    template: "<ng-content></ng-content>",
    host: { class: "bv-dialog-footer" },
    styles: [
        `
      :host(.bv-dialog-footer) {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-3);
        padding: var(--space-4) var(--space-6);
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BvDialogFooterComponent {}
